import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { refreshBilling } from "../lib/api";
import { hasBillingReturn, stripBillingParam } from "../lib/billing";

/**
 * 充值回站（billing=1）的一次性副作用：
 *   1. `POST /api/billing/refresh` 让服务端重拉上游权益；
 *   2. invalidate `["me"]`，顶栏点数 chip 立刻换新值；
 *   3. `history.replaceState` 摘掉 `billing=1`，刷新页面不会重复触发。
 *
 * 放在路由外层（`AppRoutes`）而不是某个页面：回跳地址可能是项目列表，
 * 也可能是工作台（`return` 带的是用户离开时那一页）。
 *
 * `useRef` 是必须的：StrictMode 下 effect 会 setup → cleanup → setup，
 * 没有它就会打两次刷新。
 */
export function BillingReturn() {
  const queryClient = useQueryClient();
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current || typeof window === "undefined") return;
    if (!hasBillingReturn(window.location.search)) return;
    consumed.current = true;

    void (async () => {
      try {
        // 刷新失败（未连接 / 上游抖）不阻塞回站：保留旧快照，页面照常可用。
        await refreshBilling({ timeoutMs: 8_000 });
      } catch {
        // 静默：余额晚一点刷新不影响用户继续用。
      }
      // 先标脏（refetchType:none 避免和「初始 me 还在飞」合并掉），再强制拉一次：
      // 只 invalidate 的话，在途请求会被合并，点数 chip 可能还是刷新前的值。
      await queryClient.invalidateQueries({ queryKey: ["me"], refetchType: "none" });
      await queryClient.refetchQueries(
        { queryKey: ["me"], type: "active" },
        { throwOnError: false },
      );
      window.history.replaceState(
        window.history.state,
        "",
        stripBillingParam(window.location.pathname, window.location.search, window.location.hash),
      );
    })();
  }, [queryClient]);

  return null;
}
